import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import './Contact.css';
import { translations } from '../translations';
import { t } from '../i18n';

const Contact = ({ language = 'bg' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Input validation
  const validateForm = () => {
    const errors = [];
    
    // Validate name
    if (!formData.name || formData.name.trim().length === 0) {
      errors.push(language === 'bg' ? 'Името е задължително' : 'Name is required');
    } else if (formData.name.trim().length > 100) {
      errors.push(language === 'bg' ? 'Името трябва да бъде по-малко от 100 символа' : 'Name must be less than 100 characters');
    } else if (!/^[a-zA-Zа-яА-Я\s\-'\.]+$/u.test(formData.name.trim())) {
      errors.push(language === 'bg' ? 'Името съдържа невалидни символи' : 'Name contains invalid characters');
    }
    
    // Validate email
    if (!formData.email || formData.email.trim().length === 0) {
      errors.push(language === 'bg' ? 'Имейлът е задължителен' : 'Email is required');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        errors.push(language === 'bg' ? 'Невалиден формат на имейл' : 'Invalid email format');
      } else if (formData.email.length > 255) {
        errors.push(language === 'bg' ? 'Имейлът трябва да бъде по-малък от 255 символа' : 'Email must be less than 255 characters');
      }
    }
    
    // Validate message
    if (!formData.message || formData.message.trim().length === 0) {
      errors.push(language === 'bg' ? 'Съобщението е задължително' : 'Message is required');
    } else if (formData.message.trim().length < 10) {
      errors.push(language === 'bg' ? 'Съобщението трябва да бъде поне 10 символа' : 'Message must be at least 10 characters');
    } else if (formData.message.length > 5000) {
      errors.push(language === 'bg' ? 'Съобщението трябва да бъде по-малко от 5000 символа' : 'Message must be less than 5000 characters');
    }
    
    return errors;
  };
  
  // Sanitize input
  const sanitizeInput = (value) => {
    if (typeof value !== 'string') return '';
    return value
      .replace(/[<>]/g, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+=/gi, '')
      .trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    // Validate form
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setSubmitMessage(validationErrors.join('\n'));
      setIsSubmitting(false);
      return;
    }
    
    // Sanitize form data
    const sanitizedData = {
      name: sanitizeInput(formData.name),
      email: sanitizeInput(formData.email).toLowerCase(),
      message: sanitizeInput(formData.message)
    };

    // EmailJS Configuration - use environment variables
    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      // Fallback to mailto if EmailJS not configured
      try {
        const subject = encodeURIComponent(`Contact Form Message from ${sanitizedData.name}`);
        const body = encodeURIComponent(
          `Name: ${sanitizedData.name}\n` +
          `Email: ${sanitizedData.email}\n` +
          `Message: ${sanitizedData.message}`
        );
        
        const companyEmail = 'teditashev16@gmail.com';
        const mailtoLink = `mailto:${companyEmail}?subject=${subject}&body=${body}`;
        
        window.location.href = mailtoLink;
        setSubmitMessage(language === 'bg' 
          ? '⚠️ Имейл клиентът е отворен. Моля, завършете изпращането на имейла. За автоматична доставка е необходима настройка на EmailJS.' 
          : '⚠️ Email client opened. Please complete sending the email. For automatic delivery, EmailJS setup is needed.');
        setFormData({ name: '', email: '', message: '' });
      } catch (error) {
        setSubmitMessage(language === 'bg' 
          ? 'Грешка при отваряне на имейл клиента. Моля, опитайте отново.' 
          : 'Error opening email client. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // EmailJS sending
    try {
      const templateParams = {
        from_name: sanitizedData.name,
        from_email: sanitizedData.email,
        message: sanitizedData.message,
        to_email: 'teditashev16@gmail.com'
      };

      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);
      
      setSubmitMessage(language === 'bg' 
        ? '✅ Съобщението е изпратено успешно! Ще се свържем с вас скоро.' 
        : '✅ Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitMessage(language === 'bg' 
        ? '❌ Неуспешно изпращане на съобщение. Моля, опитайте отново или се свържете с нас директно.' 
        : '❌ Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <section className="contact-main-section">
        <div className="contact-info-sidebar">
          <h1>{t(translations[language].contact.title)}</h1>
          <div className="contact-info-card">
            <h2>{t(translations[language].contact.phone.title)}</h2>
            <a href="tel:+359877488410">+359 877 488 410</a>
          </div>
          <div className="contact-info-card">
            <h2>{t(translations[language].contact.email.title)}</h2>
            <a href="mailto:info@exorevivebulgaria.com">info@exorevivebulgaria.com</a>
          </div>
          <div className="contact-info-card">
            <h2>{t(translations[language].contact.social.title)}</h2>
            <a href="https://www.instagram.com/exorevive_bulgaria/" target="_blank" rel="noopener noreferrer" className="social-link instagram">Instagram</a>
            <a href="https://www.facebook.com/ExosomesBulgaria" target="_blank" rel="noopener noreferrer" className="social-link facebook">Facebook</a>
          </div>
        </div>

        <div className="contact-form-container">
          <h2>{t(translations[language].contact.form.title)}</h2>
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <label htmlFor="name">{t(translations[language].contact.form.name.label)}</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder={t(translations[language].contact.form.name.placeholder)}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">{t(translations[language].contact.form.email.label)}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder={t(translations[language].contact.form.email.placeholder)}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="message">{t(translations[language].contact.form.message.label)}</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows="6"
                placeholder={t(translations[language].contact.form.message.placeholder)}
              ></textarea>
            </div>
            
            <button type="submit" disabled={isSubmitting} className="submit-btn">
              {isSubmitting 
                ? t(translations[language].contact.form.button.sending)
                : t(translations[language].contact.form.button.send)}
            </button>
            
            {submitMessage && (
              <div className={`submit-message ${submitMessage.includes('Error') || submitMessage.includes('Грешка') ? 'error' : 'success'}`}>
                {submitMessage}
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact; 